/**
 * @file Showcase P2 预览区块
 * @description Admin Layout / DataTable / RequestTable / Tree / Steps 等预览。
 *
 * @whenToUse 仅由 `src/pages/showcase-page.tsx` 组合引用。
 * @whenNotToUse 业务页面或通用 ui 控件实现。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity,
  CheckCircle2,
  Heading,
  LayoutTemplate,
  ListTree,
  Rows3,
  Search,
  Split,
  Table2,
} from 'lucide-react'
import dataTablePrompt from '@/docs/components/data-table.md?raw'
import requestTablePrompt from '@/docs/components/request-table.md?raw'
import descriptionsPrompt from '@/docs/components/descriptions.md?raw'
import pageHeaderPrompt from '@/docs/components/page-header.md?raw'
import progressPrompt from '@/docs/components/progress.md?raw'
import resultPrompt from '@/docs/components/result.md?raw'
import segmentedPrompt from '@/docs/components/segmented.md?raw'
import statisticPrompt from '@/docs/components/statistic.md?raw'
import stepsPrompt from '@/docs/components/steps.md?raw'
import timelinePrompt from '@/docs/components/timeline.md?raw'
import treePrompt from '@/docs/components/tree.md?raw'
import treeSelectPrompt from '@/docs/components/tree-select.md?raw'
import adminLayoutPrompt from '@/docs/components/admin-layout.md?raw'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table.tsx'
import { Descriptions } from '@/components/ui/descriptions.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Input } from '@/components/ui/input.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import { RequestTable } from '@/components/ui/request-table.tsx'
import { Progress } from '@/components/ui/progress.tsx'
import { Result } from '@/components/ui/result.tsx'
import { Segmented } from '@/components/ui/segmented.tsx'
import { ShowcaseCard } from '@/components/ui/showcase-card.tsx'
import { Statistic } from '@/components/ui/statistic.tsx'
import { Steps } from '@/components/ui/steps.tsx'
import { Timeline } from '@/components/ui/timeline.tsx'
import { Tree, type TreeNode } from '@/components/ui/tree.tsx'
import { TreeSelect } from '@/components/ui/tree-select.tsx'

const treeData: TreeNode[] = [
  {
    key: 'eng',
    title: '工程',
    children: [
      { key: 'eng-fe', title: '前端' },
      { key: 'eng-be', title: '后端' },
    ],
  },
  {
    key: 'ops',
    title: '运营',
    children: [
      { key: 'ops-growth', title: '增长' },
      { key: 'ops-support', title: '客服' },
    ],
  },
]

export function ShowcaseP2Sections() {
  return (
    <>
      <ShowcaseCard
        id="admin-layout"
        title="Admin Layout"
        description="Sidebar + Topbar + 内容区壳层；完整演示见首页 `/`。"
        icon={<LayoutTemplate className="size-4" aria-hidden />}
        prompt={adminLayoutPrompt}
      >
        <AdminLayoutCard />
      </ShowcaseCard>

      <ShowcaseCard
        id="page-header"
        title="PageHeader"
        description="页头：面包屑 + 标题 + 操作。"
        icon={<Heading className="size-4" aria-hidden />}
        prompt={pageHeaderPrompt}
      >
        <PageHeader
          title="订单详情"
          description="查看与处理客户订单。"
          breadcrumb={[
            { key: 'a', label: '首页', href: '/' },
            { key: 'b', label: '订单' },
            { key: 'c', label: '详情' },
          ]}
          extra={<Button variant="secondary">导出</Button>}
        />
      </ShowcaseCard>

      <ShowcaseCard
        id="segmented"
        title="Segmented"
        description="分段控制器。"
        icon={<Split className="size-4" aria-hidden />}
        prompt={segmentedPrompt}
      >
        <SegmentedDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="descriptions-statistic"
        title="Descriptions / Statistic"
        description="描述列表与统计数值。"
        icon={<Rows3 className="size-4" aria-hidden />}
        prompt={`${descriptionsPrompt}\n\n---\n\n${statisticPrompt}`}
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <Descriptions
            title="用户信息"
            items={[
              { key: 'n', label: '姓名', children: 'Ling' },
              { key: 'r', label: '角色', children: 'Admin' },
              {
                key: 'e',
                label: '邮箱',
                children: 'ling@example.com',
                span: 2,
              },
            ]}
          />
          <div className="grid grid-cols-2 gap-4 rounded-xl border border-border bg-raised p-4">
            <Statistic title="今日活跃" value={1286} suffix="人" />
            <Statistic
              title="转化率"
              value={3.2}
              suffix="%"
              description="较昨日 +0.4%"
            />
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard
        id="progress-steps-timeline"
        title="Progress / Steps / Timeline"
        description="进度、步骤与时间线。"
        icon={<Activity className="size-4" aria-hidden />}
        prompt={`${progressPrompt}\n\n---\n\n${stepsPrompt}\n\n---\n\n${timelinePrompt}`}
      >
        <div className="space-y-6">
          <Progress percent={64} />
          <Progress percent={100} status="success" />
          <Steps
            current={1}
            items={[
              { key: '1', title: '填写', description: '基本信息' },
              { key: '2', title: '确认', description: '核对内容' },
              { key: '3', title: '完成', description: '提交成功' },
            ]}
          />
          <Timeline
            items={[
              {
                key: '1',
                title: '创建订单',
                description: '用户提交了新订单',
                time: '10:20',
              },
              {
                key: '2',
                title: '支付成功',
                description: '微信支付',
                time: '10:22',
              },
              {
                key: '3',
                title: '发货中',
                time: '昨天',
              },
            ]}
          />
        </div>
      </ShowcaseCard>

      <ShowcaseCard
        id="result"
        title="Result"
        description="结果页块。"
        icon={<CheckCircle2 className="size-4" aria-hidden />}
        prompt={resultPrompt}
      >
        <Result
          status="success"
          title="提交成功"
          description="我们已收到你的申请，将在 1–2 个工作日内处理。"
          extra={
            <>
              <Button>返回列表</Button>
              <Button variant="secondary">查看详情</Button>
            </>
          }
        />
      </ShowcaseCard>

      <ShowcaseCard
        id="tree-tree-select"
        title="Tree / TreeSelect"
        description="树形列表与树选择。"
        icon={<ListTree className="size-4" aria-hidden />}
        prompt={`${treePrompt}\n\n---\n\n${treeSelectPrompt}`}
      >
        <TreeTreeSelectDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="data-table"
        title="DataTable"
        description="排序、行选择、固定列、分页、加载遮罩。"
        icon={<Table2 className="size-4" aria-hidden />}
        prompt={dataTablePrompt}
      >
        <DataTableDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="request-table"
        title="RequestTable"
        description="搜索表单 + 请求封装 + DataTable + 可选分页（演示为本地 mock）。"
        icon={<Search className="size-4" aria-hidden />}
        prompt={requestTablePrompt}
      >
        <RequestTableDemo />
      </ShowcaseCard>
    </>
  )
}

function AdminLayoutCard() {
  const navigate = useNavigate()
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button onClick={() => navigate('/')}>打开后台首页</Button>
      <p className="text-sm text-muted">
        侧栏折叠、顶栏搜索、PageHeader + DataTable 已组合。
      </p>
    </div>
  )
}

function SegmentedDemo() {
  const [range, setRange] = useState('week')
  return (
    <Segmented
      value={range}
      onChange={setRange}
      options={[
        { value: 'day', label: '日' },
        { value: 'week', label: '周' },
        { value: 'month', label: '月' },
      ]}
    />
  )
}

function TreeTreeSelectDemo() {
  const [checked, setChecked] = useState<string[]>(['eng-fe'])
  const [dept, setDept] = useState('eng-fe')
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-border bg-raised p-3">
        <Tree
          treeData={treeData}
          checkable
          defaultExpandedKeys={['eng', 'ops']}
          checkedKeys={checked}
          onCheck={setChecked}
        />
      </div>
      <div className="space-y-2">
        <TreeSelect
          treeData={treeData}
          value={dept}
          onChange={setDept}
          placeholder="选择部门"
        />
        <p className="text-xs text-muted">当前：{dept}</p>
      </div>
    </div>
  )
}

function DataTableDemo() {
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>([])
  type DemoRow = {
    id: string
    name: string
    role: string
    dept: string
    city: string
    email: string
  }
  const rows: DemoRow[] = [
    {
      id: '1',
      name: 'Alice',
      role: 'Admin',
      dept: '工程',
      city: '上海',
      email: 'alice@example.com',
    },
    {
      id: '2',
      name: 'Bob',
      role: 'Editor',
      dept: '设计',
      city: '北京',
      email: 'bob@example.com',
    },
    {
      id: '3',
      name: 'Carol',
      role: 'Viewer',
      dept: '运营',
      city: '深圳',
      email: 'carol@example.com',
    },
  ]
  const columns: DataTableColumn<DemoRow>[] = [
    {
      key: 'name',
      title: '姓名',
      dataIndex: 'name',
      sortable: true,
      fixed: 'left',
      width: 100,
    },
    { key: 'role', title: '角色', dataIndex: 'role', sortable: true, width: 100 },
    { key: 'dept', title: '部门', dataIndex: 'dept', width: 100 },
    { key: 'city', title: '城市', dataIndex: 'city', width: 100 },
    {
      key: 'email',
      title: '邮箱',
      dataIndex: 'email',
      maxWidth: 140,
    },
    {
      key: 'tag',
      title: '标记',
      width: 100,
      fixed: 'right',
      render: (row) => <Badge variant="accent">{row.role}</Badge>,
    },
  ]
  return (
    <DataTable
      columns={columns}
      data={rows}
      rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
      pagination={{
        page,
        pageSize: 10,
        total: 3,
        onChange: setPage,
      }}
    />
  )
}

type RequestDemoRow = {
  id: string
  name: string
  city: string
}

type RequestDemoQuery = {
  keyword: string
  city: string
}

const REQUEST_DEMO_ROWS: RequestDemoRow[] = [
  { id: '1', name: 'Alice', city: '上海' },
  { id: '2', name: 'Bob', city: '北京' },
  { id: '3', name: 'Carol', city: '深圳' },
  { id: '4', name: 'Dave', city: '上海' },
  { id: '5', name: 'Eve', city: '杭州' },
  { id: '6', name: 'Frank', city: '北京' },
  { id: '7', name: 'Grace', city: '深圳' },
  { id: '8', name: 'Heidi', city: '上海' },
]

/**
 * Showcase 本地 mock 列表请求（模拟 @/api 分页接口）。
 * @param params 查询与分页
 */
async function mockRequestDemoList(params: {
  keyword?: string
  city?: string
  pageNum?: number
  pageSize?: number
}) {
  await new Promise((resolve) => window.setTimeout(resolve, 280))
  const keyword = params.keyword?.trim() ?? ''
  const city = params.city?.trim() ?? ''
  const filtered = REQUEST_DEMO_ROWS.filter((row) => {
    const matchKeyword =
      keyword === '' || row.name.toLowerCase().includes(keyword.toLowerCase())
    const matchCity = city === '' || row.city.includes(city)
    return matchKeyword && matchCity
  })
  const pageNum = params.pageNum ?? 1
  const pageSize = params.pageSize ?? 5
  const start = (pageNum - 1) * pageSize
  return {
    rows: filtered.slice(start, start + pageSize),
    total: filtered.length,
  }
}

function RequestTableDemo() {
  const columns: DataTableColumn<RequestDemoRow>[] = [
    { key: 'name', title: '姓名', dataIndex: 'name', sortable: true },
    { key: 'city', title: '城市', dataIndex: 'city' },
  ]

  return (
    <RequestTable<RequestDemoRow, RequestDemoQuery>
      request={mockRequestDemoList}
      pageSize={5}
      columns={columns}
      beforeRequest={(params) => ({
        ...params,
        keyword: params.keyword?.trim() ?? '',
      })}
      searchForm={{
        initialValues: { keyword: '', city: '' },
        items: ({ values, setField }) => (
          <>
            <FormField label="姓名" className="w-44">
              <Input
                value={values.keyword}
                onChange={(event) => setField('keyword', event.target.value)}
                placeholder="关键字"
              />
            </FormField>
            <FormField label="城市" className="w-36">
              <Input
                value={values.city}
                onChange={(event) => setField('city', event.target.value)}
                placeholder="城市"
              />
            </FormField>
          </>
        ),
      }}
    />
  )
}
